import React, { useEffect, useState } from "react";
import { Trans } from "@lingui/macro";
import { useDispatch, useSelector } from "react-redux";
import {
  IdPosConfigurationState,
  typePosConfigurationFilters,
} from "model/dashboardPosConfiguration/dPosConfigurationReducer";
import {
  dPosConfigurationClearFiltersAC,
  dPosConfigurationDeleteItemSagaAC,
  dPosConfigurationSetDataSagaAC,
  dPosConfigurationSetDetailItemAC,
  dPosConfigurationSetFiltersAC,
} from "model/dashboardPosConfiguration/dPosConfigurationActionCreators";
import { useInputChange } from "hooks/useInputChange";
import { ViewDataPosConfiguration } from "./ViewComponent";

// Type
export type typePosConfiguration = {
  variant?: string;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeDataPosConfigurationRowActions = {
  id?: string;
  title: JSX.Element;
  action: (id: string) => void;
}[];

// Component
export const DataPosConfiguration: React.FC<typePosConfiguration> = ({
  variant,
  setSidebarOpen,
}) => {
  const dispatch = useDispatch();

  // DateFilter
  const [isActiveFilters, setIsActiveFilters] = useState(false);
  const [openFilterBar, setOpenFilterBar] = useState(false);
  const filters = useSelector(
    (state: { dPosConfiguration: IdPosConfigurationState }) =>
      state.dPosConfiguration.filters,
  );

  // Did mount & Did unmount
  useEffect(() => {
    return () => {
      dispatch(dPosConfigurationClearFiltersAC());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(dPosConfigurationSetDataSagaAC(filters));
    // eslint-disable-next-line
  }, [filters]);

  // Search panel
  const searchName = useSelector(
    (state: { dPosConfiguration: IdPosConfigurationState }) =>
      state.dPosConfiguration.filters.name,
  );
  const {
    value: searchValue,
    setValue: setSearchValue,
    changeValueHandler: onChangeSearchValue,
  } = useInputChange(searchName || "");
  const onClearSearch = () => {
    dispatch(dPosConfigurationSetFiltersAC({ name: null }));
    setSearchValue("");
  };
  const onEnterSearch = () => {
    const filters: typePosConfigurationFilters = {
      name: searchValue.trim(),
    };
    dispatch(dPosConfigurationSetFiltersAC(filters));
  };

  // Popup details
  const [openPopupDetails, setOpenPopupDetails] = useState(false);

  // Popup add
  const [openPopupAdd, setOpenPopupAdd] = useState(false);

  // Popup edit
  const [openPopupEdit, setOpenPopupEdit] = useState(false);

  // Delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const onDeleteConfirmHandler = () => {
    dispatch(dPosConfigurationDeleteItemSagaAC(currentItem!.id, filters));
    setOpenDeleteDialog(false);
  };
  const onDeleteCancelHandler = () => {
    setOpenDeleteDialog(false);
  };

  // Current item
  const currentItem = useSelector(
    (state: { dPosConfiguration: IdPosConfigurationState }) =>
      state.dPosConfiguration.detailItem,
  );

  // Make table item actions
  const rowsActions: typeDataPosConfigurationRowActions = [
    {
      title: <Trans>Edit</Trans>,
      action: (id: string) => {
        dispatch(dPosConfigurationSetDetailItemAC(id));
        setOpenPopupEdit(true);
      },
    },
    {
      id: "delete",
      title: <Trans>Delete</Trans>,
      action: (id: string) => {
        dispatch(dPosConfigurationSetDetailItemAC(id));
        setOpenDeleteDialog(true);
      },
    },
  ];

  // Make header Actions
  const onClickAdd = () => {
    setOpenPopupAdd(true);
  };

  return (
    <ViewDataPosConfiguration
      variant={variant}
      setSidebarOpen={setSidebarOpen}
      onClickAdd={onClickAdd}
      openFilterBar={openFilterBar}
      setOpenFilterBar={setOpenFilterBar}
      openPopupDetails={openPopupDetails}
      setOpenPopupDetails={setOpenPopupDetails}
      openPopupAdd={openPopupAdd}
      setOpenPopupAdd={setOpenPopupAdd}
      openPopupEdit={openPopupEdit}
      setOpenPopupEdit={setOpenPopupEdit}
      openDeleteDialog={openDeleteDialog}
      setOpenDeleteDialog={setOpenDeleteDialog}
      onDeleteConfirmHandler={onDeleteConfirmHandler}
      onDeleteCancelHandler={onDeleteCancelHandler}
      searchValue={searchValue}
      onChangeSearchValue={onChangeSearchValue}
      onEnterSearch={onEnterSearch}
      onClearSearch={onClearSearch}
      rowsActions={rowsActions}
      currentItem={currentItem}
      isActiveFilters={isActiveFilters}
      setIsActiveFilters={setIsActiveFilters}
    />
  );
};
